import { Injectable } from '@angular/core';
import { TimeRange, BenchmarkDashboardDatapoint, ValueAggregation } from './benchmark-dashboard.data.interface';
import { DataPointsService } from '@datapoints';
import { TimeDateConverterService } from '@converter';

interface DataResult  {
    date: number,
    value: any
}


@Injectable()
export class BenchmarkDashboardDataService {
    constructor(private datapointService: DataPointsService, public dateService: TimeDateConverterService){}

    
    public async getPlantData(plantId: string, datapointId: string, from: number, to: number, aggregation: ValueAggregation, rangeParts: number[]) {
        const data = await this.datapointService.getDataPointHistoriesBundel([plantId + ';' + datapointId], from, to);
        return this.aggregate(data[0].value, aggregation, rangeParts);
    }

    public async getData(datapoints: BenchmarkDashboardDatapoint[],  from: number, to: number, aggregation: ValueAggregation, range: TimeRange, compareRange: TimeRange) {
        const output = [];
        const rangeParts = this.getRangeParts(from, to, range);
        for(const datapoint of datapoints){
            const result = await this.getPlantData(datapoint.plantId, datapoint.datapointId, from, to, aggregation, rangeParts);
            
            let compareValues = [];
            if(compareRange) {
                const compareDateFrom = this.getCompareDate(from, compareRange);
                const compareDateTo = this.getCompareDate(to, compareRange);
                const compareRangeParts = this.getCompareRange(rangeParts, compareRange)
                compareValues = await this.getPlantData(
                    datapoint.plantId,
                    datapoint.datapointId,
                    compareDateFrom,
                    compareDateTo,
                    aggregation,
                    compareRangeParts
                );
            }

            if(result) {
                result.forEach((entry, index) => {
                    output[index] = output[index] || {};
                    output[index][datapoint.plantId + '_' +  datapoint.datapointId] = entry;
                    output[index][datapoint.plantId + '_' +  datapoint.datapointId + '_compare'] = compareValues[index];
                    output[index]['desc_' + datapoint.plantId + '_' +  datapoint.datapointId] = 'Name';
                    output[index].group = range + ':' + compareRange + ':' + index + ':' + rangeParts[index] + ':' + compareValues[index];
                })
                
            }
            
        }
        return output;
    }

    private getDaysInMonth(year, month) {
        return new Date(year, month + 1, 0).getDate();
    }

    public getCompareDate(date: number, compareRange: TimeRange) {

        const currentDate = new Date(date);
        const newDate = new Date(date);

        switch(compareRange) {
            case TimeRange.day:
                newDate.setDate(currentDate.getDate() - 1)
                return newDate.getTime();
            case TimeRange.week:
                newDate.setDate(currentDate.getDate() - 7)
                return newDate.getTime();
            case TimeRange.month:
                const newMonth = currentDate.getMonth() - 1;
                const newYear = (newMonth < 0) ? currentDate.getFullYear() - 1 : currentDate.getFullYear();
                newDate.setFullYear(newYear);
                const daysInNewMonth = this.getDaysInMonth(newYear, newMonth);
                const day = currentDate.getDate() > daysInNewMonth ? daysInNewMonth :  currentDate.getDate();
                newDate.setDate(day);
                if(newMonth < 0) {
                    newDate.setMonth(newMonth + 12)
                }else{
                    newDate.setMonth(newMonth)
                }
                return newDate.getTime();
            case TimeRange.year:
                newDate.setDate(currentDate.getDate())
                newDate.setMonth(currentDate.getMonth())
                newDate.setFullYear(currentDate.getFullYear() - 1)
                return newDate.getTime();
        }
    }

    private aggregate(values: DataResult[],  aggregation: ValueAggregation, rangeParts: number[]) {
      
        switch(aggregation) {
            case ValueAggregation.average:
                return this.getAverageValues(values, rangeParts);
            case ValueAggregation.commulative:
                return this.getCommulativeValues(values, rangeParts);
            case ValueAggregation.max:
                return this.getMaxValues(values, rangeParts);
            case ValueAggregation.min:
                return this.getMinValues(values, rangeParts)
            default:
                return this.getRawValues(values, rangeParts);
        }
    }


    private getNextTime(time, range) {
        return range.find((current) => {
            if(current !== null && current >  time) {
                return true
            }
            return false;
        })
    }


    private filterTimeRages(values, current, next) {
        return values.filter((entry) => {
              
            const result =  (next && entry.date >= current && entry.date < next)
                || (!next && entry.date >= current);
            
            return result;
        })
    }

    private handleEmptyResult(result, ranges, current, index) {
        if(!result.length && index > 0) {
            result = [{
                date : current + 1,
                value : null
            }];
        }
        if(!result.length) {
            ranges[current] =  [{
                date : current + 1,
                value : null
            }];
        }else{
            ranges[current] = result;
        }
    }

    private mapValuesToRange(values: DataResult[], range: number[]) {
        const ranges = {};
        if(range.length === 1) {
            range.forEach((current, index) => {
                const result = values.filter((entry) => entry.date >= current);
                ranges[current] = result;
            })
            return ranges;
        }else{
            
            range.forEach((current, index) => {
                if(current === null) {
                    ranges[current] = null;
                    return
                }
              
                const next = this.getNextTime(current, range);
                const result = this.filterTimeRages(values, current, next)


                this.handleEmptyResult(result, ranges, current, index);
                
            })
        }
        return ranges
    }

    private getRawValues(values: DataResult[], range: number[]){
        const rangeValues = this.mapValuesToRange(values, range) ;
     
        return range.map((current, index) => {
            const lastValue = rangeValues[current][rangeValues[current].length - 1];
            return lastValue.value;
        });
    }

    
    private getCommulativeValues(values: DataResult[], range: number[]) {
        const rangeValues = this.mapValuesToRange(values, range);
        return range.map((currentRange) => {
            if(!this.hasRangeValues(rangeValues[currentRange])) {
                return null;
            }
            const sum = rangeValues[currentRange].reduce((accumulator, current) => accumulator + (current.value), 0);
            return sum;
        });
    }

    private getAverageValues(values: DataResult[], range: number[]) {
        const rangeValues = this.mapValuesToRange(values, range);
        return range.map((currentRange, index) => {
            if(!this.hasRangeValues(rangeValues[currentRange])) {
                return null;
            }
            const sum = rangeValues[currentRange].reduce((accumulator, current) => accumulator + (current.value), 0);
            return sum / rangeValues[currentRange].length;
        });
    }

    private getMaxValues(values: DataResult[], range: number[]) {
        const rangeValues = this.mapValuesToRange(values, range);
        return range.map((currentRange) => {
            if(!this.hasRangeValues(rangeValues[currentRange])) {
                return null;
            }
            const sum = rangeValues[currentRange].reduce((accumulator, current) => {
                if(accumulator < (current.maxVal || current.value)){
                    accumulator = (current.maxVal || current.value);
                }
                return accumulator;
            }, 0);
            return sum;
        })
    }

    private hasRangeValues(values) {
        if(!values || !values.length) {
            return false;
        }

        if(values.length === 1 && Object.keys(values[0]).length === 1) {
            return false;
        }

        return true;
    }

    private getMinValues(values: DataResult[], range: number[]) {
    
        const rangeValues = this.mapValuesToRange(values, range);

        return range.map((currentRange) => {
            let sum = null;
            if(!this.hasRangeValues(rangeValues[currentRange])) {
                return null;
            }
            for(const current of rangeValues[currentRange]) {
                const currentValue = current.minVal ?? current.value;
                if(sum === null || currentValue < sum) {
                    sum = currentValue
                }
            }
            return sum;
        })
    }


    private getMonthRange(from: number, to: number) {
        const fromDate = new Date(from);
        const toDate = new Date(to);
        let diff = toDate.getMonth() - fromDate.getMonth();
        const yearDiff = toDate.getFullYear() - fromDate.getFullYear();

        if(yearDiff > 0) {
            diff *= -1;
            diff = (yearDiff * 12) - diff;
        }
       
        const output = [from];
        for(let i = 1; i <= diff; i++) {
           
            let nextMonth = fromDate.getMonth() + i;
            let nextYear = fromDate.getFullYear();
            if(nextMonth > 11) {
                nextMonth -= 12;
                nextYear++;
            }
            const nextDate = new Date(nextYear, nextMonth, 1, 0, 0, 0);
            output.push(nextDate.getTime())
        }

        return output;
    }


    private getYearRange(from: number, to: number) {
        const fromDate = new Date(from);
        const toDate = new Date(to);
        const diff = toDate.getFullYear() - fromDate.getFullYear();
        const output = [from];
        for(let i = 1; i <= diff; i++) {
         
            const nextYear = fromDate.getFullYear() + i;
            const nextDate = new Date(nextYear, 0, 1, 0, 0, 0);
            output.push(nextDate.getTime())
        }
        return output;
    }



    private getWeekRange(from: number, to: number) {
        const toDate = new Date(to);
        const fromDate = new Date(from);
        toDate.setHours(0, 0, 0, 0)
        const fromWeek = this.dateService.getWeekNumberFromTimestamp(from);
        const toWeek = this.dateService.getWeekNumberFromTimestamp(to);
        const yearDiff = toDate.getFullYear() - fromDate.getFullYear();
        let diff = toWeek - fromWeek;
        if(yearDiff > 0) {
            diff *= -1;
            diff = (yearDiff * 52) - diff;
        }
        const output = [from];
        let yearCounter = 0;
        for(let i = 1; i <= diff; i++) {
            let nextWeek = fromWeek + i;
            if(!(nextWeek % 53)) {
                yearCounter++;
            }
            nextWeek = (nextWeek) - (yearCounter * 52);
            const next = this.dateService.getTimestampFromWeekNumber(nextWeek, fromDate.getFullYear() + yearCounter);
            next.setHours(0, 0, 0, 0);
            output.push(next.getTime())   
        }
        return output;
    }


    private getDayRange(from: number, to: number) { 
        const output = [];
        let i = 0;
        while(true) {
            const currentDate = new Date(from)
            currentDate.setDate( currentDate.getDate() + i);
            const time = currentDate.getTime();
            if(time >= to) {
                return output;
            }else{
                
                output.push(time);
            }
            i++;
        }
    }

    private getRangeParts(from: number, to: number, range: TimeRange){
        switch(range){
            case TimeRange.day:
                return this.getDayRange(from, to)
            case TimeRange.week:
                return this.getWeekRange(from, to);
            case TimeRange.month:
                return this.getMonthRange(from, to);
            case TimeRange.year:
                return this.getYearRange(from, to);
        }
    }


    public getCompareRange(defaultRanges: number[], range: TimeRange) {
        return defaultRanges.map((current) => {
            const currentDate = new Date(current);
            const nextDate = new Date(current);
            switch(range){
                case TimeRange.day:
                    nextDate.setDate(currentDate.getDate() - 1);
                    return nextDate.getTime(); 
                case TimeRange.week:
                    nextDate.setDate(currentDate.getDate() - 7);
                    return nextDate.getTime();
                case TimeRange.month:
                    nextDate.setMonth(currentDate.getMonth() - 1)
                    if(nextDate.getDate() !== currentDate.getDate()) {
                        return null;
                    }
                    return nextDate.getTime();
                case TimeRange.year:
                    nextDate.setFullYear(currentDate.getFullYear() - 1);
                    if(nextDate.getDate() !== currentDate.getDate()) {
                        return null;
                    }
                    return nextDate.getTime();
            }
        })
    }

}